import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';
import { useSelectedProject, usePruneMissingProject } from './projectDeepLink';
import { rowClassName, FIXED_FEE_PROJECT_TYPES } from './Configuration';
import { useRegisterDirty, useNavGuardConfirm } from '../unsavedChangesGuard';

// Feature gate, not a source toggle — there is no legacy path for this page. Same pattern and same
// reason as ProjectSchedule.js's REACT_APP_SCHEDULING_ENABLED: the nav item already exists and has
// been showing a placeholder since v1, so an un-flipped environment keeps showing exactly that
// rather than a half-wired screen.
const BPA_ENABLED = process.env.REACT_APP_BPA_ENABLED === 'true';

const STATUS_BADGE = {
  'Not Scheduled': 'badge-gray',
  Scheduled: 'badge-blue',
  Completed: 'badge-green',
  'Follow-up Needed': 'badge-yellow',
  Canceled: 'badge-gray',
  'Not Applicable': 'badge-gray',
};

// The value the project picker holds while the BPA Library editor is open, instead of a project id.
//
// A sentinel in the same dropdown rather than a separate page or nav item, at Jeremy's request — "when
// in maintenance mode we are able to select 'BPA Library' as a project (to keep the UI the same)". It
// is offered only to an admin with maintenance mode on (ADR 0020); the API enforces the admin half
// independently (@Roles('admin') on the template write routes), so this is the workflow gate, not the
// security one. Prefixed and suffixed so it can never collide with a uuid.
export const LIBRARY_PROJECT_ID = '__bpa-library__';

// The free-text columns of the detail grid, in the order the Clark County template puts them.
//
// **This covers 5 of the grid's 11 columns, not all of them.** `detailTopic` is frozen and lives with
// the other pinned columns below; Status, Config, Internal notes, Follow up and Owner need their own
// control (a select, a checkbox) rather than an input or a textarea, so they are still written out in
// both the header and the body. Adding a free-text column here gets a matching header for free; adding
// one of the other kind still means editing two places.
//
// The three "what was planned / what was said" columns are the widest deliberately (Jeremy,
// 2026-07-30): they hold paragraphs, and at 240px a BA was scrolling sideways to read one answer. The
// two narrower ones after them hold a sentence or two.
// Every column here holds prose, so they are all textareas — there is no `kind` discriminator any more.
// It existed only for `detailTopic`, which is now a pinned column rendered explicitly alongside the
// other frozen cells.
// `expand: true` grows the textarea on focus (see .cell-area-expand:focus in App.css) instead of
// relying on a fixed 2-row box — these three hold paragraphs, per Jeremy's 2026-07-30 ask.
// `futureProcessNotes` ("Future process / Aumentum") is dropped from the grid at his request; the
// column stays in the schema (real data may exist), just not rendered here.
const DETAIL_COLUMNS = [
  { key: 'discussionPoints', label: 'Discussion points', width: 380, expand: true },
  { key: 'customerQuestions', label: 'Customer questions', width: 380, expand: true },
  { key: 'customerResponse', label: 'Customer response', width: 400, expand: true },
  { key: 'decisionOutcome', label: 'Decision / outcome', width: 220 },
];

/**
 * Excel's freeze panes, for a `<table>`.
 *
 * Given the widths of the leading columns to pin, returns the props each of those cells needs. The
 * sticky `left` offset is **derived from the widths**, so the two cannot drift — the failure mode
 * otherwise is a column pinned a few pixels off its own edge, which reads as a rendering glitch rather
 * than as a stale constant. Widths are pinned with `min`/`max` as well as `width` because `width` alone
 * is only a hint to auto table layout: one long topic title would stretch the column out from under
 * its offset.
 *
 * You cannot freeze a middle column on its own — everything to its left has to come with it, which is
 * how Excel behaves too. So "freeze the Topic column" means freezing the checkbox, the expander and the
 * number alongside it.
 */
export function freezePanes(widths) {
  let left = 0;
  return widths.map((width, index) => {
    const props = {
      className: `is-frozen${index === widths.length - 1 ? ' is-frozen-edge' : ''}`,
      style: { left, width, minWidth: width, maxWidth: width },
    };
    left += width;
    return props;
  });
}

// Exported: CustomerBpa.js's read-only grid reuses this for the same sticky-column look.
/** Merge a frozen cell's props with a class the cell already carries (`cell-check`, `cell-num`, …). */
export function frozen(panes, index, className) {
  const pane = panes[index];
  return {
    className: className ? `${className} ${pane.className}` : pane.className,
    style: pane.style,
  };
}

// select · expand · # · Topic — the session grid runs past 1800px and Topic is the only column that
// says which row you are in.
const SESSION_PANES = freezePanes([34, 34, 48, 240]);
// select · # · Detail topic — same idea one level down.
const DETAIL_PANES = freezePanes([34, 38, 200]);
// select/expand · # · Title, for the library editor's topic grid.
const LIBRARY_TOPIC_PANES = freezePanes([34, 48, 260]);
// # · Detail topic, for the library editor's detail-template grid.
const LIBRARY_DETAIL_PANES = freezePanes([44, 240]);

// Sortable session columns. `get` returns the value to compare, normalized so nulls sort last in
// either direction rather than clumping at whichever end the comparator happens to put them.
const SESSION_SORTS = {
  sessionNumber: (s) => s.sessionNumber ?? Number.MAX_SAFE_INTEGER,
  topic: (s) => (s.topic ?? '').toLowerCase(),
  module: (s) => (s.module?.code ?? '￿').toLowerCase(),
  status: (s) => (s.status ?? '').toLowerCase(),
  scheduledDate: (s) => s.scheduledDate ?? '￿',
  conductedBy: (s) => (s.conductedBy || '￿').toLowerCase(),
};

/** Date input wants `yyyy-mm-dd`; the API returns an ISO timestamp. */
export function toDateInput(value) {
  return value ? String(value).slice(0, 10) : '';
}

/** Depth-tagged flat list, so the module picker can indent without recursing while rendering. */
export function flattenModules(nodes, depth = 0) {
  return (nodes ?? []).flatMap((n) => [
    { id: n.id, name: n.name, code: n.code, depth },
    ...flattenModules(n.children, depth + 1),
  ]);
}

/**
 * An emptied date or iteration cell has to be sent as `null`, not `''`.
 *
 * `<input type="date">` and a `<select>` both report a cleared value as an empty string, which the
 * API's `@IsISO8601()`/`@IsUUID()` reject with a 400 — so clearing one would fail the whole save with
 * a validation error about the field the user had just deliberately emptied. `null` is the API's
 * "unset this", and it is distinct from omitting the key, which means "leave it alone".
 */
export function normalizePatch(patch) {
  const out = { ...patch };
  for (const key of ['scheduledDate', 'bpaIterationTaskId']) {
    if (out[key] === '') out[key] = null;
  }
  return out;
}

/**
 * The sessions and detail rows customer-facing at milestone sign-off, for the two sheets of the
 * exported workbook.
 *
 * **Excludes internal-only columns** (`configurationNeeded`, `configurationNotes`, `followUpNeeded`,
 * `followUpOwner`, `followUpNotes`, session-level `notes`) — this is exactly the field allow-list ADR
 * 0017 anticipated for a customer view, applied here rather than waiting on customer login. Getting
 * this list wrong leaks internal working notes into a document a customer keeps as their own record.
 *
 * `moduleIds` scopes to one iteration's modules ("some modules for each DAS"), matching the export
 * panel's multi-select. A session with no module (a hand-added, customer-specific one) is **always
 * included** — it belongs to no module, so a module filter has nothing to test it against, and
 * silently dropping it from every export would be a worse surprise than an unfiltered one.
 */
export function buildReportRows(sessions, moduleIds) {
  const restrict = moduleIds && moduleIds.size > 0;
  const included = (sessions ?? []).filter(
    (s) => !restrict || !s.module || moduleIds.has(s.module.id),
  );

  const sessionRows = included.map((s) => ({
    '#': s.sessionNumber ?? '',
    Topic: s.topic,
    Module: s.module ? `${s.module.code} — ${s.module.name}` : '',
    Status: s.status,
    Date: toDateInput(s.scheduledDate),
    Start: s.startTime ?? '',
    End: s.endTime ?? '',
    Onsite: s.deliveryMethod === 'Onsite' ? 'Yes' : 'No',
    'Led by': s.conductedBy ?? '',
  }));

  const detailRows = included.flatMap((s) =>
    (s.details ?? []).map((d) => ({
      Session: s.topic,
      Module: s.module ? `${s.module.code} — ${s.module.name}` : '',
      '#': d.detailNumber ?? '',
      'Detail topic': d.detailTopic,
      'Discussion points': d.discussionPoints ?? '',
      'Customer questions': d.customerQuestions ?? '',
      'Customer response': d.customerResponse ?? '',
      'Decision / outcome': d.decisionOutcome ?? '',
      Status: d.status,
    })),
  );

  return { sessionRows, detailRows };
}

/** Filesystem-safe: a project or module name routinely has spaces and punctuation a browser's save
 * dialog would otherwise choke on or silently mangle. */
function safeFileSegment(value) {
  return String(value ?? '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Writes the two-sheet workbook and triggers the browser download. Not exported/tested — it's a thin
 * wrapper over `XLSX.writeFile`; `buildReportRows` above is where the logic worth testing lives.
 */
function downloadReportXlsx(sessions, moduleIds, projectName) {
  const { sessionRows, detailRows } = buildReportRows(sessions, moduleIds);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sessionRows), 'Sessions');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailRows), 'Discussion Detail');
  const suffix = moduleIds && moduleIds.size > 0 ? '' : ' - All Modules';
  XLSX.writeFile(wb, `${safeFileSegment(projectName)} BPA Report${safeFileSegment(suffix)}.xlsx`);
}

/** Filter-dropdown sentinel for "Tax and all its submodules" -- not a real module id, so it can't
 * collide with one. */
export const TAX_ALL_SUBMODULES_VALUE = 'tax-all-submodules';

/** Tax's children in `BPA_MODULE_TAXONOMY` (packages/shared). Duplicated, not imported -- `apps/web`
 * can't import `packages/shared` (ADR 0019), same reason the session-status/detail-status lists are
 * hand-copied elsewhere in this file. */
const TAX_SUBMODULE_CODES = ['AR', 'CASH', 'DELQ', 'DIST', 'LEVY', 'SA', 'TACC'];

/** Sort + filter, applied in that order. Pure, so it can be tested without rendering. */
export function visibleSessions(sessions, { text, status, moduleId, sortKey, sortDir }) {
  const needle = (text ?? '').trim().toLowerCase();
  const taxAll = moduleId === TAX_ALL_SUBMODULES_VALUE;
  let rows = (sessions ?? []).filter((s) => {
    if (status && s.status !== status) return false;
    if (taxAll) {
      if (s.module?.code !== 'TAX' && !TAX_SUBMODULE_CODES.includes(s.module?.code)) return false;
    } else if (moduleId && s.module?.id !== moduleId) return false;
    if (!needle) return true;
    return [s.topic, s.module?.name, s.module?.code, s.conductedBy]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(needle));
  });

  const get = SESSION_SORTS[sortKey];
  if (get) {
    rows = rows.slice().sort((a, b) => {
      const [x, y] = [get(a), get(b)];
      const cmp = x < y ? -1 : x > y ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }
  return rows;
}

/**
 * The rollups for whatever rows are actually on screen.
 *
 * The API returns the same figures for the whole workbook, and that is what the header used to show —
 * so filtering down to one module left "24 sessions · 6 open follow-ups" sitting above three rows
 * (Jeremy, 2026-08-27). Recomputing from the filtered rows is also what keeps the header honest while
 * edits are still pending, since those live in the rows and not yet on the server.
 *
 * Mirrors BpaWorkbookService.summarize — the definitions have to stay in step, so change both.
 */
export function summarizeSessions(sessions) {
  const rows = sessions ?? [];
  const details = rows.flatMap((s) => s.details ?? []);
  const answered = details.filter((d) => d.status !== 'Open').length;
  return {
    sessionCount: rows.length,
    scheduledCount: rows.filter((s) => s.scheduledDate).length,
    completedCount: rows.filter((s) => s.status === 'Completed').length,
    configurationNeeded: details.filter((d) => d.configurationNeeded).length,
    openFollowUps: details.filter((d) => d.followUpNeeded).length,
    // Sessions with no rows at all are excluded from the denominator rather than counted as
    // complete — reporting an empty agenda item as 100% is arithmetic that hides a gap.
    percentAnswered: details.length ? Math.round((answered / details.length) * 100) : 0,
  };
}

/**
 * What the note above the grid should say about the zone its times are written in.
 *
 * Times are stored without a zone (ADR 0019) and are the county's own local time, so the zone has to be
 * stated once somewhere — this is that statement, derived from the account's state.
 *
 * Returns `null` for no state, which the caller renders as an honest "we don't know" rather than a
 * default. Guessing Eastern for a county with no state recorded would be wrong roughly three times in
 * four, and silently so.
 *
 * `state` is a row of `GET /bpa/vocabulary`'s `states` array — `apps/web` cannot import
 * `packages/shared`, so the list is served rather than duplicated (same reason
 * `/scheduling/vocabulary` exists).
 */
export function timeZoneNote(state) {
  if (!state) return null;
  const caveats = [];
  if (state.alsoSpans?.length) {
    caveats.push(
      `${state.name} also spans ${state.alsoSpans.join(' and ')} — check which zone this county is in.`,
    );
  }
  if (state.noDaylightSaving) {
    caveats.push(`${state.name} does not observe daylight saving.`);
  }
  return { zone: `${state.timeZone} Time`, stateName: state.name, caveats };
}

/**
 * Time-booking buckets carried over from OnePlan, not projects anyone runs a workshop for. Matched by
 * name because nothing on Project distinguishes a bucket from real delivery work — the same set and
 * the same reasoning as ProjectSchedule.js's NON_SCHEDULABLE_PROJECT_NAMES. `projectType` alone isn't
 * enough: Misc (Plug) was backfilled as POC by name, so only the name catches it.
 */
const NON_BPA_PROJECT_NAMES = new Set(['Non-Billable Time', 'Misc (Plug)']);

/**
 * Projects worth offering in the BPA picker.
 *
 * Fixed-fee only (Jeremy, 2026-08-27; B&B added 2026-10-06) — narrower than the Project Schedule
 * picker, which also allows Internal. A BPA is scoped discovery on a fixed-fee implementation; T&M
 * is hourly work and Internal is the non-billable bucket, and neither runs workshops. A row with no
 * `projectType` predates the column and is treated as the 'POC' default rather than hidden — hiding
 * on a missing field would silently shrink the list.
 */
export function selectableProjects(projects, showArchived) {
  return projects.filter(
    (p) =>
      !NON_BPA_PROJECT_NAMES.has(p.name) &&
      FIXED_FEE_PROJECT_TYPES.includes(p.projectType ?? 'POC') &&
      (showArchived || p.projectStatus !== 'Archived'),
  );
}

/**
 * Text for one `<option>`. Text, because an `<option>` renders nothing else — no badge, no color —
 * so the "no sessions yet" marker has to be a character.
 *
 * `sessionProjectIds` is null until the coverage call answers. Marking every project empty in the
 * meantime would be a visible lie that then flickers away, so nothing is claimed until it's known.
 */
export function projectOptionLabel(project, sessionProjectIds) {
  const marker = sessionProjectIds && !sessionProjectIds.has(project.id) ? '◇ ' : '';
  // Only meaningful once archived rows are actually being shown, and harmless when they aren't.
  const archived = project.projectStatus === 'Archived' ? ' · archived' : '';
  return `${marker}${project.name}${archived}`;
}

function BPATracker() {
  // Restores whichever project was last selected on any of these pages (ADR 0042), or a
  // `?project=<id>` deep link (ADR 0038). Archived projects are shown when one arrives, so it
  // resolves whatever its status. LIBRARY_PROJECT_ID is excluded from persistence -- it isn't a
  // project, and every other page on this convention would otherwise inherit an id it can't resolve.
  const [projectId, setProjectId] = useSelectedProject(LIBRARY_PROJECT_ID);

  // Backs the "Led by" / "Owner" fields: a <datalist> so typing filters down to a real user while
  // still accepting free text (a workshop lead or follow-up owner isn't always someone in the system).
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sessionProjectIds, setSessionProjectIds] = useState(null); // null until loaded — see projectOptionLabel
  const [showArchived, setShowArchived] = useState(!!projectId);
  const [workbook, setWorkbook] = useState(null);
  const [vocabulary, setVocabulary] = useState(null);
  const [moduleTree, setModuleTree] = useState([]);
  const [iterations, setIterations] = useState([]);

  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedSessions, setSelectedSessions] = useState(() => new Set());
  const [selectedDetails, setSelectedDetails] = useState(() => new Set());

  // Pending edits, keyed by row id. The grid writes here and to the rendered rows; Save flushes them.
  const [dirtyDetails, setDirtyDetails] = useState({});
  const [dirtySessions, setDirtySessions] = useState({});
  // Mirrors the two states above for loadWorkbook's preserveDirty path (a stable-identity
  // useCallback can't read fresh state directly without becoming a dependency of the project-switch
  // effect below it, which would refetch on every keystroke instead of only on a real project change).
  const dirtyDetailsRef = useRef(dirtyDetails);
  const dirtySessionsRef = useRef(dirtySessions);
  useEffect(() => { dirtyDetailsRef.current = dirtyDetails; }, [dirtyDetails]);
  useEffect(() => { dirtySessionsRef.current = dirtySessions; }, [dirtySessions]);

  const [sort, setSort] = useState({ key: 'sessionNumber', dir: 'asc' });
  const [filter, setFilter] = useState({ text: '', status: '', moduleId: '' });

  const [setupOpen, setSetupOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Which modules the export is scoped to. Empty means "all" — the panel starts here rather than
  // pre-checking everything, so opening it and immediately exporting does the obvious thing.
  const [exportModuleIds, setExportModuleIds] = useState(() => new Set());
  const [seedPreview, setSeedPreview] = useState(null);
  // Which planned sessions to actually create — defaults to everything a fresh preview found, so
  // leaving every row checked and pressing the button still seeds the whole plan as before.
  const [seedSelectedIds, setSeedSelectedIds] = useState(() => new Set());
  const [sowResult, setSowResult] = useState(null);
  const [confirming, setConfirming] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const dirtyCount = Object.keys(dirtyDetails).length + Object.keys(dirtySessions).length;
  const confirmNavigation = useNavGuardConfirm();
  useRegisterDirty(dirtyCount > 0);

  // The library editor is offered to any internal user (was admin + maintenance mode, ADR 0020). Both facts come
  // from AppProvider, which already fetches /auth/me and /maintenance once for the whole app — no extra
  // round trip here, and the nav, the maintenance banner and this all read the same values.
  const { currentUser } = useApp();
  // Any internal user may maintain the library (2026-11-01; was admin + maintenance mode, ADR 0020).
  const libraryAvailable = !!currentUser;
  const libraryMode = projectId === LIBRARY_PROJECT_ID;

  // Maintenance mode can be switched off from the admin page while this tab sits open on the library.
  // Falling back to no selection is better than leaving an editor on screen whose writes the API would
  // still accept but which nobody has agreed is a safe time to use.
  useEffect(() => {
    if (libraryMode && !libraryAvailable) setProjectId('');
  }, [libraryMode, libraryAvailable, setProjectId]);

  useEffect(() => {
    (async () => {
      try {
        const [projectList, vocab, tree, userList, coverage] = await Promise.all([
          apiFetch('/projects'),
          apiFetch('/bpa/vocabulary'),
          apiFetch('/modules/tree'),
          apiFetch('/users'),
          apiFetch('/bpa/projects-with-sessions'),
        ]);
        setProjects(projectList);
        setVocabulary(vocab);
        setModuleTree(tree);
        setUsers(userList);
        setSessionProjectIds(new Set(coverage.projectIds));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  // `preserveDirty`: true for every in-page action (add/delete/seed/scope-change) that refreshes
  // the *same* project after a write -- only a real navigation away (the effect below, on
  // `projectId` changing) should discard an in-progress edit sitting elsewhere in the grid. This
  // is the general fix for the bug `addDetail` ("+ Add row") used to hit on its own: any of these
  // action handlers reloading the workbook wiped every other unsaved cell on the page.
  const loadWorkbook = useCallback(async (id, { preserveDirty = false } = {}) => {
    // The library sentinel is not a project id — there is no workbook to fetch for it, and asking for
    // one would 400 on a malformed uuid. Anything left over from the previous selection is dropped so
    // the workbook UI does not linger behind the editor.
    if (id === LIBRARY_PROJECT_ID) {
      setWorkbook(null);
      setError('');
      return;
    }
    if (!id) return;
    setBusy('loading');
    setError('');
    try {
      const [wb, iters] = await Promise.all([
        apiFetch(`/bpa/workbook/${id}`),
        apiFetch(`/bpa/iterations/${id}`),
      ]);
      // Re-apply whatever's still pending onto the freshly-fetched sessions/details -- otherwise
      // preserving the dirty *bookkeeping* below would leave it pointing at typed text the grid no
      // longer shows (the fresh fetch overwrote the cell back to its last-saved value), so Save
      // would silently re-apply a value the screen had already moved past.
      const freshWorkbook = !preserveDirty
        ? wb
        : {
            ...wb,
            sessions: wb.sessions.map((s) => {
              const sessionPatch = dirtySessionsRef.current[s.id];
              const session = sessionPatch ? { ...s, ...sessionPatch } : s;
              return {
                ...session,
                details: session.details.map((d) => {
                  const detailPatch = dirtyDetailsRef.current[d.id];
                  return detailPatch ? { ...d, ...detailPatch } : d;
                }),
              };
            }),
          };
      setWorkbook(freshWorkbook);
      setIterations(iters);
      // Keep the picker's ◇ honest without a second round trip: this reloads after every write, so
      // seeding or clearing a project moves its marker straight away. The coverage call itself is
      // only fetched once, on mount.
      setSessionProjectIds((prev) => {
        if (!prev) return prev;
        const hasSessions = wb.sessions.length > 0;
        if (prev.has(id) === hasSessions) return prev;
        const updated = new Set(prev);
        if (hasSessions) updated.add(id);
        else updated.delete(id);
        return updated;
      });
      // Anything pending or selected belonged to the project being navigated away from -- but not
      // when this reload is just an in-page action refreshing the same project (preserveDirty).
      if (!preserveDirty) {
        setDirtyDetails({});
        setDirtySessions({});
        setSelectedSessions(new Set());
        setSelectedDetails(new Set());
      }
      setSeedPreview(null);
      setSeedSelectedIds(new Set());
      setSowResult(null);
    } catch (e) {
      setError(e.message);
      setWorkbook(null);
    } finally {
      setBusy('');
    }
  }, []);

  useEffect(() => {
    loadWorkbook(projectId);
  }, [projectId, loadWorkbook]);

  const visibleProjects = useMemo(
    () => selectableProjects(projects, showArchived),
    [projects, showArchived],
  );

  // Un-ticking "show archived" while an archived project is selected would leave the select on a
  // value none of its options carry, which browsers render as a blank box. Drop back to the
  // placeholder rather than to another project — silently swapping in someone else's workbook is
  // worse than asking again. The library sentinel is exempt (it is not a project row) and has its
  // own effect above; the loaded guard is ADR 0038's.
  usePruneMissingProject(
    projectId, setProjectId, visibleProjects, projects.length > 0, LIBRARY_PROJECT_ID,
  );

  // Edits are applied to the rendered rows immediately and remembered separately. Waiting for the
  // server before showing a typed character is what makes a web grid feel unlike a spreadsheet.
  const editDetail = (sessionId, detailId, key, value) => {
    setWorkbook((wb) => ({
      ...wb,
      sessions: wb.sessions.map((s) =>
        s.id !== sessionId
          ? s
          : { ...s, details: s.details.map((d) => (d.id === detailId ? { ...d, [key]: value } : d)) },
      ),
    }));
    setDirtyDetails((prev) => ({ ...prev, [detailId]: { ...prev[detailId], [key]: value } }));
  };

  const editSession = (sessionId, key, value) => {
    setWorkbook((wb) => ({
      ...wb,
      sessions: wb.sessions.map((s) => (s.id === sessionId ? { ...s, [key]: value } : s)),
    }));
    setDirtySessions((prev) => ({ ...prev, [sessionId]: { ...prev[sessionId], [key]: value } }));
  };

  // Separate from editSession: the grid needs the whole `module` object (id/code/name) to render the
  // read side immediately, but the patch sent to the API is just `moduleId` — a bare id string can't
  // stand in for both.
  const editSessionModule = (sessionId, moduleId) => {
    const module = moduleId ? flatModules.find((m) => m.id === moduleId) : null;
    setWorkbook((wb) => ({
      ...wb,
      sessions: wb.sessions.map((s) => (s.id === sessionId ? { ...s, module } : s)),
    }));
    setDirtySessions((prev) => ({ ...prev, [sessionId]: { ...prev[sessionId], moduleId: moduleId || null } }));
  };

  const run = async (label, fn, successMessage) => {
    setBusy(label);
    setError('');
    setNotice('');
    try {
      const message = await fn();
      if (message ?? successMessage) setNotice(message ?? successMessage);
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy('');
      setConfirming('');
    }
  };

  const saveAll = () =>
    run('saving', async () => {
      const detailRows = Object.entries(dirtyDetails).map(([id, patch]) => ({ id, ...patch }));
      // Detail rows go in one call: the API takes the whole grid's worth in a single transaction, so
      // a save either lands completely or not at all. Sessions have no bulk route — there are few of
      // them and they are edited one at a time in practice.
      await Promise.all([
        detailRows.length
          ? apiFetch('/bpa-details', { method: 'PATCH', body: JSON.stringify({ rows: detailRows }) })
          : null,
        ...Object.entries(dirtySessions).map(([id, patch]) =>
          apiFetch(`/bpa-sessions/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(normalizePatch(patch)),
          }),
        ),
      ]);
      const saved = `Saved ${detailRows.length} row(s) and ${Object.keys(dirtySessions).length} session(s).`;
      // Cleared only after the writes succeed — a failed save must keep the typing rather than
      // discarding it because the network blipped.
      setDirtyDetails({});
      setDirtySessions({});
      await loadWorkbook(projectId, { preserveDirty: true });
      return saved;
    });

  const runSeed = (commit, topicTemplateIds) =>
    run(commit ? 'seeding' : 'previewing', async () => {
      const result = await apiFetch(`/bpa/seed/${projectId}`, {
        method: 'POST',
        body: JSON.stringify({ commit, ...(topicTemplateIds ? { topicTemplateIds } : {}) }),
      });
      if (!commit) {
        setSeedPreview(result);
        // Everything the preview found starts checked, so a BA who wants the whole plan (the common
        // case) can press the button without touching a single checkbox.
        setSeedSelectedIds(new Set(result.planned.map((p) => p.topicTemplateId)));
        return '';
      }
      setSeedPreview(null);
      setSeedSelectedIds(new Set());
      await loadWorkbook(projectId, { preserveDirty: true });
      return (
        `Created ${result.sessionsCreated} session(s) and ${result.detailsCreated} row(s).` +
        (result.skipped.length ? ` Skipped ${result.skipped.length} already seeded.` : '')
      );
    });

  const removeUnstarted = () =>
    run('clearing', async () => {
      const result = await apiFetch(`/bpa/seed/${projectId}/unstarted`, { method: 'DELETE' });
      await loadWorkbook(projectId, { preserveDirty: true });
      return (
        `Removed ${result.deletedSessions} unstarted session(s).` +
        (result.retained.length
          ? ` Kept ${result.retained.length} that had work in them: ${result.retained
              .map((r) => `${r.topic} (${r.reason})`)
              .join('; ')}`
          : '')
      );
    });

  const addFromSow = () =>
    run('sow', async () => {
      const result = await apiFetch(`/project-modules/from-sow/${projectId}`, { method: 'POST' });
      setSowResult(result);
      await loadWorkbook(projectId, { preserveDirty: true });
      return `Added ${result.created} module(s) from the SOW.`;
    });

  const addModuleToScope = (moduleId) =>
    moduleId &&
    run('scope', async () => {
      await apiFetch('/project-modules', {
        method: 'POST',
        body: JSON.stringify({ projectId, moduleId }),
      });
      await loadWorkbook(projectId, { preserveDirty: true });
      return '';
    });

  const setModuleIteration = (projectModuleId, taskId) =>
    run('scope', async () => {
      await apiFetch(`/project-modules/${projectModuleId}`, {
        method: 'PATCH',
        body: JSON.stringify(normalizePatch({ bpaIterationTaskId: taskId })),
      });
      await loadWorkbook(projectId, { preserveDirty: true });
      return '';
    });

  const removeModuleFromScope = (projectModuleId) =>
    run('scope', async () => {
      await apiFetch(`/project-modules/${projectModuleId}`, { method: 'DELETE' });
      await loadWorkbook(projectId, { preserveDirty: true });
      return '';
    });

  const deleteSelectedSessions = () =>
    run('deleting', async () => {
      const ids = [...selectedSessions];
      await apiFetch('/bpa-sessions', { method: 'DELETE', body: JSON.stringify({ ids }) });
      setSelectedSessions(new Set());
      await loadWorkbook(projectId, { preserveDirty: true });
      return `Deleted ${ids.length} session(s) and their discussion rows.`;
    });

  // A session already in the library comes back as a 409; run() puts its message on screen.
  const addSelectedToLibrary = () =>
    run('library', async () => {
      const ids = [...selectedSessions];
      await apiFetch('/bpa-sessions/add-to-library', { method: 'POST', body: JSON.stringify({ ids }) });
      setSelectedSessions(new Set());
      await loadWorkbook(projectId, { preserveDirty: true });
      return `Added ${ids.length} session(s) to the BPA Library.`;
    });

  const deleteSelectedDetails = () =>
    run('deleting', async () => {
      const ids = [...selectedDetails];
      await apiFetch('/bpa-details', { method: 'DELETE', body: JSON.stringify({ ids }) });
      setSelectedDetails(new Set());
      await loadWorkbook(projectId, { preserveDirty: true });
      return `Deleted ${ids.length} discussion row(s).`;
    });

  const deleteSession = (id) =>
    run('deleting', async () => {
      await apiFetch(`/bpa-sessions/${id}`, { method: 'DELETE' });
      await loadWorkbook(projectId, { preserveDirty: true });
      return 'Session deleted, along with its discussion rows.';
    });

  const deleteDetail = (id) =>
    run('deleting', async () => {
      await apiFetch(`/bpa-details/${id}`, { method: 'DELETE' });
      await loadWorkbook(projectId, { preserveDirty: true });
      return 'Discussion row deleted.';
    });

  const filterModuleId = () => {
    if (filter.moduleId !== TAX_ALL_SUBMODULES_VALUE) return filter.moduleId || undefined;
    const taxModules = sessionModules.filter((m) => m.code === 'TAX' || TAX_SUBMODULE_CODES.includes(m.code));
    return (taxModules.find((m) => m.code === 'TAX') ?? taxModules[0])?.id;
  };

  const addSession = () =>
    run('adding', async () => {
      // Sequence 1 so a hand-added session sorts to the top of the grid where it can be found and
      // filled in, rather than after fifty seeded ones. It takes the module being filtered on, or the
      // first of them for "Tax - All Submodules" (the parent), so the filter doesn't hide it
      // (Catherine Knipp, 2026-10-06).
      const created = await apiFetch('/bpa-sessions', {
        method: 'POST',
        body: JSON.stringify({ projectId, topic: 'New session', sequence: 1, moduleId: filterModuleId() }),
      });
      await loadWorkbook(projectId, { preserveDirty: true });
      // Text/status filters can't be satisfied by a prefill, so those still reset.
      setFilter((f) => ({ ...f, text: '', status: '' }));
      setExpanded((prev) => new Set(prev).add(created.id));
      return '';
    });

  const addDetail = (sessionId) =>
    run('adding', async () => {
      const session = workbook.sessions.find((s) => s.id === sessionId);
      const nextNumber = Math.max(0, ...(session?.details ?? []).map((d) => d.detailNumber ?? 0)) + 1;
      const created = await apiFetch('/bpa-details', {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          detailTopic: 'New discussion row',
          detailNumber: nextNumber,
          sequence: nextNumber,
        }),
      });
      // Appended locally rather than a full loadWorkbook() — a reload resets dirtyDetails/
      // dirtySessions (see loadWorkbook), which would silently drop anything the BA had typed but not
      // yet saved elsewhere in the grid.
      setWorkbook((wb) => ({
        ...wb,
        sessions: wb.sessions.map((s) =>
          s.id === sessionId ? { ...s, details: [...s.details, created] } : s,
        ),
      }));
      return '';
    });

  const flatModules = useMemo(() => flattenModules(moduleTree), [moduleTree]);

  // Keyed on `modulesInScope`, not `workbook` — `workbook` is replaced on every keystroke in the
  // grid, so depending on it would rebuild these constantly and the memo would never hit.
  const inScopeIds = useMemo(
    () => new Set((workbook?.modulesInScope ?? []).map((m) => m.module.id)),
    [workbook?.modulesInScope],
  );

  const rows = useMemo(
    () => visibleSessions(workbook?.sessions, { ...filter, sortKey: sort.key, sortDir: sort.dir }),
    [workbook?.sessions, filter, sort],
  );

  // Over `rows`, not `workbook.sessions` — the cards describe what's in front of you.
  const summary = useMemo(() => summarizeSessions(rows), [rows]);
  const filterActive = !!(filter.text.trim() || filter.status || filter.moduleId);

  // Distinct modules actually present on sessions, for the filter dropdown. Filtering by a module
  // with no sessions would just blank the grid with no way to tell why.
  const sessionModules = useMemo(() => {
    const seen = new Map();
    for (const s of workbook?.sessions ?? []) {
      if (s.module && !seen.has(s.module.id)) seen.set(s.module.id, s.module);
    }
    return [...seen.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [workbook?.sessions]);

  // The zone every time in the grid is written in, from the customer's state. Read off the project list
  // (which includes `account`) rather than the workbook payload, which carries only the project's own
  // columns.
  const zoneNote = useMemo(() => {
    const account = projects.find((p) => p.id === projectId)?.account;
    const code = (account?.state ?? '').trim().toUpperCase();
    return timeZoneNote((vocabulary?.states ?? []).find((s) => s.code === code));
  }, [projects, projectId, vocabulary]);

  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const allVisibleSelected = rows.length > 0 && rows.every((s) => selectedSessions.has(s.id));
  // Judged against the *visible* rows: with a filter on, "expand all" reasonably means the rows in
  // front of you. Collapsing clears the whole set rather than only those rows, since a collapsed row
  // nobody can see is not a state worth preserving.
  const allVisibleExpanded = rows.length > 0 && rows.every((s) => expanded.has(s.id));
  const toggleExpandAll = () =>
    setExpanded(allVisibleExpanded ? new Set() : new Set(rows.map((s) => s.id)));

  if (!BPA_ENABLED) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">📋</div>
        <div className="placeholder-title">Business Process Analysis</div>
        <div className="placeholder-sub">
          Built, but not switched on in this environment. Set <code>REACT_APP_BPA_ENABLED=true</code>{' '}
          to enable it.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Definition &amp; Analysis</div>
        <h1 className="page-title">Business Process Analysis</h1>
        <div className="page-subtitle">
          Business process analysis sessions and their discussion records, seeded from the standard
          library for each module in scope.
        </div>
      </div>

      {/* Backs the "Led by" / "Owner" inputs below — one shared list, referenced by id. */}
      <datalist id="bpa-user-list">
        {users.map((u) => (
          <option key={u.id} value={u.name} />
        ))}
      </datalist>

      {error && <div className="notice notice-error">{error}</div>}
      {notice && <div className="notice notice-info">{notice}</div>}

      <div className="card">
        <div className="sheet-toolbar" style={{ marginBottom: 0 }}>
          <select
            className="form-select"
            style={{ maxWidth: 340 }}
            value={projectId}
            onChange={(e) => {
              if (confirmNavigation()) setProjectId(e.target.value);
              else e.target.value = projectId;
            }}
            aria-label="Project"
          >
            <option value="">Select a project…</option>
            {visibleProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {projectOptionLabel(p, sessionProjectIds)}
              </option>
            ))}
            {/* Not a project. See LIBRARY_PROJECT_ID — the standard library is edited through the same
                picker so the screen a library admin uses is the screen they already know. */}
            {libraryAvailable && (
              <option value={LIBRARY_PROJECT_ID}>■ BPA Library (standard topics)</option>
            )}
          </select>
          {/* A checkbox rather than another filter: archived projects are the exception you
              occasionally look back at, not a dimension worth its own control. */}
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
              color: 'var(--text-mid)', cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                // Un-ticking this can prune the selected project out from under the workbook
                // (usePruneMissingProject) exactly like switching projects directly would --
                // checking it ON only ever adds options to the picker, so only that direction
                // needs the guard.
                if (e.target.checked || confirmNavigation()) setShowArchived(e.target.checked);
                else e.target.checked = showArchived;
              }}
            />
            Show archived
          </label>
          <div className="spacer" />
          {libraryMode && (
            <span className="cell-read" style={{ padding: 0, fontSize: 12 }}>
              Editing the shared library — changes affect every project seeded from here onwards
            </span>
          )}
          {workbook && (
            <>
              <span className="cell-read" style={{ padding: 0, fontSize: 12 }}>
                {workbook.modulesInScope.filter((m) => m.inScope).length} module(s) in scope
              </span>
              <button className="btn btn-outline" onClick={() => setSetupOpen(true)}>
                Modules &amp; seeding…
              </button>
            </>
          )}
        </div>
        {/* Explains the marker above rather than leaving "◇" to be guessed at, and says why the list
            is shorter than the project list elsewhere. Only shown once coverage has answered. */}
        {sessionProjectIds && (
          <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 8 }}>
            Fixed-fee (POC) projects only.
            {visibleProjects.some((p) => !sessionProjectIds.has(p.id)) &&
              ' ◇ marks a project with no BPA sessions yet.'}
          </div>
        )}
      </div>

      {busy === 'loading' && <div className="notice notice-info">Loading…</div>}

      {libraryMode && <LibraryEditor flatModules={flatModules} vocabulary={vocabulary} />}

      {workbook && (
        <>
          <div className="stat-row">
            <Stat value={summary.sessionCount} label="Sessions" />
            <Stat value={summary.scheduledCount} label="Scheduled" />
            <Stat value={summary.completedCount} label="Completed" />
            <Stat value={`${summary.percentAnswered}%`} label="Rows answered" />
            <Stat
              value={summary.configurationNeeded}
              label="Config needed"
              tone={summary.configurationNeeded > 0 ? 'warn' : ''}
            />
            <Stat
              value={summary.openFollowUps}
              label="Open follow-ups"
              tone={summary.openFollowUps > 0 ? 'alert' : ''}
            />
          </div>
          {/* Says which numbers you're looking at. Without it a filtered header is indistinguishable
              from a project that really does only have three sessions. */}
          {filterActive && (
            <div style={{ fontSize: 12, color: 'var(--text-mid)', margin: '-8px 0 16px' }}>
              Filtered — these figures cover {summary.sessionCount} of{' '}
              {workbook.summary.sessionCount} session(s).
            </div>
          )}

          <div className="card">
            <div className="sheet-toolbar">
              <input
                className="form-input"
                style={{ maxWidth: 240 }}
                placeholder="Filter topic, module or lead…"
                value={filter.text}
                onChange={(e) => setFilter((f) => ({ ...f, text: e.target.value }))}
              />
              <select
                className="form-select"
                style={{ maxWidth: 170 }}
                value={filter.status}
                onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {(vocabulary?.sessionStatuses ?? []).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select
                className="form-select"
                style={{ maxWidth: 170 }}
                value={filter.moduleId}
                onChange={(e) => setFilter((f) => ({ ...f, moduleId: e.target.value }))}
                aria-label="Filter by module"
              >
                <option value="">All modules</option>
                <option value={TAX_ALL_SUBMODULES_VALUE}>Tax - All Submodules</option>
                {sessionModules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.name}
                  </option>
                ))}
              </select>
              {(filter.text || filter.status || filter.moduleId) && (
                <button
                  className="btn btn-outline"
                  onClick={() => setFilter({ text: '', status: '', moduleId: '' })}
                >
                  Clear filters
                </button>
              )}
              <div className="spacer" />
              <button
                className="btn btn-outline"
                onClick={toggleExpandAll}
                disabled={rows.length === 0}
                title="Show or hide every session's discussion rows"
              >
                {allVisibleExpanded ? 'Collapse all' : 'Expand all'}
              </button>
              <button className="btn btn-outline" onClick={addSession} disabled={!!busy}>
                + Session
              </button>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setExportModuleIds(new Set());
                  setExportOpen(true);
                }}
                disabled={workbook.sessions.length === 0}
                title="Build a customer-facing .xlsx report, optionally scoped to one iteration's modules"
              >
                Export…
              </button>
              <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyCount}>
                {busy === 'saving' ? 'Saving…' : dirtyCount ? `Save ${dirtyCount} change(s)` : 'Saved'}
              </button>
            </div>

            {(selectedSessions.size > 0 || selectedDetails.size > 0) && (
              <div className="notice notice-warn" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>
                  {selectedSessions.size > 0 && `${selectedSessions.size} session(s) selected`}
                  {selectedSessions.size > 0 && selectedDetails.size > 0 && ' · '}
                  {selectedDetails.size > 0 && `${selectedDetails.size} row(s) selected`}
                </span>
                <div className="spacer" />
                {confirming === 'bulk' ? (
                  <>
                    <strong>
                      Delete
                      {selectedSessions.size > 0 &&
                        ` ${selectedSessions.size} session(s) and every row inside them`}
                      {selectedSessions.size > 0 && selectedDetails.size > 0 && ', and'}
                      {selectedDetails.size > 0 && ` ${selectedDetails.size} row(s)`}?
                    </strong>
                    <button
                      className="btn btn-outline"
                      onClick={async () => {
                        if (selectedSessions.size) await deleteSelectedSessions();
                        if (selectedDetails.size) await deleteSelectedDetails();
                      }}
                      disabled={!!busy}
                    >
                      {busy === 'deleting' ? 'Deleting…' : 'Yes, delete'}
                    </button>
                    <button className="btn btn-outline" onClick={() => setConfirming('')}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {selectedSessions.size > 0 && (
                      <button
                        className="btn btn-outline"
                        onClick={addSelectedToLibrary}
                        disabled={!!busy}
                        title="Copy the selected sessions and their discussion rows (not the answers) into the BPA Library"
                      >
                        {busy === 'library' ? 'Adding…' : 'Add to library'}
                      </button>
                    )}
                    <button className="btn btn-outline" onClick={() => setConfirming('bulk')}>
                      Delete selected
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={() => {
                        setSelectedSessions(new Set());
                        setSelectedDetails(new Set());
                      }}
                    >
                      Clear selection
                    </button>
                  </>
                )}
              </div>
            )}

            <TimeZoneNote note={zoneNote} />

            {workbook.sessions.length === 0 ? (
              <div className="cell-read" style={{ padding: 0 }}>
                No sessions yet. Open <strong>Modules &amp; seeding</strong> to add the modules in
                scope and seed from the library, or add a session by hand.
              </div>
            ) : rows.length === 0 ? (
              <div className="cell-read" style={{ padding: 0 }}>
                No sessions match the current filters.
              </div>
            ) : (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th {...frozen(SESSION_PANES, 0)}>
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={(e) =>
                            setSelectedSessions((prev) => {
                              const next = new Set(prev);
                              rows.forEach((s) =>
                                e.target.checked ? next.add(s.id) : next.delete(s.id),
                              );
                              return next;
                            })
                          }
                          aria-label="Select all visible sessions"
                        />
                      </th>
                      <th {...frozen(SESSION_PANES, 1)} />
                      <SortHeader label="#" k="sessionNumber" sort={sort} onSort={toggleSort} freeze={SESSION_PANES[2]} />
                      <SortHeader label="Topic" k="topic" sort={sort} onSort={toggleSort} freeze={SESSION_PANES[3]} />
                      <SortHeader label="Module" k="module" sort={sort} onSort={toggleSort} width={90} />
                      <SortHeader label="Status" k="status" sort={sort} onSort={toggleSort} width={150} />
                      <SortHeader label="Date" k="scheduledDate" sort={sort} onSort={toggleSort} width={130} />
                      <th style={{ width: 105 }}>Start</th>
                      <th style={{ width: 105 }}>End</th>
                      <th style={{ width: 66 }}>Onsite</th>
                      <SortHeader label="Led by" k="conductedBy" sort={sort} onSort={toggleSort} width={130} />
                      <th style={{ width: 180 }}>Meeting link</th>
                      <th style={{ width: 180 }}>Recording link</th>
                      <th style={{ width: 60 }}>Rows</th>
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s, index) => (
                      <React.Fragment key={s.id}>
                        <tr className={rowClassName(index, !!dirtySessions[s.id])}>
                          <td {...frozen(SESSION_PANES, 0, 'cell-check')}>
                            <input
                              type="checkbox"
                              checked={selectedSessions.has(s.id)}
                              onChange={() => toggle(setSelectedSessions, s.id)}
                              aria-label={`Select ${s.topic}`}
                            />
                          </td>
                          <td {...frozen(SESSION_PANES, 1)}>
                            <button
                              className="expand-btn"
                              onClick={() => toggle(setExpanded, s.id)}
                              aria-label={expanded.has(s.id) ? 'Collapse' : 'Expand'}
                              style={{ padding: '7px 10px' }}
                            >
                              {expanded.has(s.id) ? '▾' : '▸'}
                            </button>
                          </td>
                          <td {...frozen(SESSION_PANES, 2, 'cell-num')}>{s.sessionNumber ?? '—'}</td>
                          <td {...frozen(SESSION_PANES, 3)}>
                            <input
                              className="cell-input"
                              value={s.topic ?? ''}
                              onChange={(e) => editSession(s.id, 'topic', e.target.value)}
                            />
                          </td>
                          <td>
                            <select
                              className="cell-select"
                              value={s.module?.id ?? ''}
                              onChange={(e) => editSessionModule(s.id, e.target.value)}
                              aria-label={`Module for ${s.topic}`}
                            >
                              <option value="">—</option>
                              {flatModules.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {' '.repeat(m.depth * 2)}
                                  {m.code}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="cell-select"
                              value={s.status}
                              onChange={(e) => editSession(s.id, 'status', e.target.value)}
                            >
                              {(vocabulary?.sessionStatuses ?? [s.status]).map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="date"
                              className="cell-input"
                              value={toDateInput(s.scheduledDate)}
                              onChange={(e) => editSession(s.id, 'scheduledDate', e.target.value)}
                            />
                          </td>
                          {/* Free text, not a time picker, and with no zone in the value — the zone is
                              the county's own and is stated once in the note above the grid (ADR 0019).
                              A native <input type="time"> was rejected: it forces a locale-dependent
                              spinner, cannot hold the legacy values already in these columns, and is
                              slower to type into than "930".

                              Standardising happens on the API write path, not here: apps/web cannot
                              import packages/shared, so an on-blur formatter in this file would be a
                              second copy of the rules. The cell reads back as "9:30 AM" after Save,
                              which is consistent with every other column on a page where nothing is
                              live-saved. */}
                          <td>
                            <input
                              className="cell-input"
                              placeholder="930 or 9:30a"
                              title="Typed loosely and tidied on save — 930, 9:30a and 1300 all become a standard time"
                              value={s.startTime ?? ''}
                              onChange={(e) => editSession(s.id, 'startTime', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="cell-input"
                              placeholder="1100 or 11a"
                              title="Typed loosely and tidied on save — 1100, 11a and 1100am all become a standard time"
                              value={s.endTime ?? ''}
                              onChange={(e) => editSession(s.id, 'endTime', e.target.value)}
                            />
                          </td>
                          {/* Onsite is the only delivery distinction that carries operational meaning
                              (do the BAs travel), so it is a checkbox rather than a dropdown.
                              Unchecked is Remote — the default, the common case, and also how a
                              pre-existing null reads.

                              These two literals must stay in step with BPA_DELIVERY_METHODS in
                              packages/shared, which GET /bpa/vocabulary serves. They are hardcoded
                              rather than read from that payload because a two-state checkbox encodes
                              exactly two values by construction: reading them out of an array by
                              index would trade a visible literal for a silent order dependency.
                              apps/web also has no dependency on @ps-ops-hub/shared to import from. */}
                          <td className="cell-check">
                            <input
                              type="checkbox"
                              checked={s.deliveryMethod === 'Onsite'}
                              onChange={(e) =>
                                editSession(s.id, 'deliveryMethod', e.target.checked ? 'Onsite' : 'Remote')
                              }
                              aria-label="Onsite"
                            />
                          </td>
                          <td>
                            <input
                              className="cell-input"
                              list="bpa-user-list"
                              placeholder="Type to search…"
                              value={s.conductedBy ?? ''}
                              onChange={(e) => editSession(s.id, 'conductedBy', e.target.value)}
                            />
                          </td>
                          <LinkCell
                            value={s.meetingLink}
                            placeholder="Teams link"
                            onChange={(v) => editSession(s.id, 'meetingLink', v)}
                          />
                          <LinkCell
                            value={s.recordingLink}
                            placeholder="Recording URL"
                            onChange={(v) => editSession(s.id, 'recordingLink', v)}
                          />
                          <td className="cell-num">
                            <span className={`badge ${STATUS_BADGE[s.status] ?? 'badge-gray'}`}>
                              {s.details.length}
                            </span>
                          </td>
                          <td>
                            {confirming === s.id ? (
                              <button
                                className="icon-btn"
                                onClick={() => deleteSession(s.id)}
                                disabled={!!busy}
                                title="Confirm delete — removes its discussion rows too"
                              >
                                ✓?
                              </button>
                            ) : (
                              <button
                                className="icon-btn"
                                onClick={() => setConfirming(s.id)}
                                title="Delete this session and its rows"
                              >
                                ✕
                              </button>
                            )}
                          </td>
                        </tr>

                        {expanded.has(s.id) && (
                          <tr>
                            <td colSpan={15} style={{ padding: '0 0 10px 34px', background: 'var(--light-bg)' }}>
                              <DetailGrid
                                session={s}
                                vocabulary={vocabulary}
                                dirty={dirtyDetails}
                                selected={selectedDetails}
                                onToggleSelect={(id) => toggle(setSelectedDetails, id)}
                                onEdit={editDetail}
                                onAdd={() => addDetail(s.id)}
                                onDelete={deleteDetail}
                                busy={busy}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {exportOpen && workbook && (
        <ExportModal
          sessionModules={sessionModules}
          selectedIds={exportModuleIds}
          onToggle={(id) =>
            setExportModuleIds((prev) => {
              const next = new Set(prev);
              next.has(id) ? next.delete(id) : next.add(id);
              return next;
            })
          }
          onSelectAll={() => setExportModuleIds(new Set(sessionModules.map((m) => m.id)))}
          onSelectNone={() => setExportModuleIds(new Set())}
          onClose={() => setExportOpen(false)}
          onExport={() => {
            downloadReportXlsx(workbook.sessions, exportModuleIds, workbook.project.name);
            setExportOpen(false);
          }}
        />
      )}

      {setupOpen && workbook && (
        <SetupModal
          workbook={workbook}
          flatModules={flatModules}
          inScopeIds={inScopeIds}
          iterations={iterations}
          seedPreview={seedPreview}
          seedSelectedIds={seedSelectedIds}
          sowResult={sowResult}
          busy={busy}
          onClose={() => setSetupOpen(false)}
          onAddFromSow={addFromSow}
          onAddModule={addModuleToScope}
          onSetIteration={setModuleIteration}
          onRemoveModule={removeModuleFromScope}
          onPreviewSeed={() => runSeed(false)}
          onCommitSeed={() => runSeed(true, [...seedSelectedIds])}
          onCancelPreview={() => {
            setSeedPreview(null);
            setSeedSelectedIds(new Set());
          }}
          onToggleSeedRow={(id) =>
            setSeedSelectedIds((prev) => {
              const next = new Set(prev);
              next.has(id) ? next.delete(id) : next.add(id);
              return next;
            })
          }
          onSelectAllSeedRows={() =>
            setSeedSelectedIds(new Set((seedPreview?.planned ?? []).map((p) => p.topicTemplateId)))
          }
          onSelectNoneSeedRows={() => setSeedSelectedIds(new Set())}
          onRemoveUnstarted={removeUnstarted}
          onMaintainLibrary={() => {
            setSetupOpen(false);
            setProjectId(LIBRARY_PROJECT_ID);
          }}
        />
      )}
    </div>
  );
}

// `freeze` is one entry from a freezePanes() array; pass it instead of `width` for a pinned column, and
// it brings the sticky offset and the pinned width with it.
// Exported: CustomerBpa.js's read-only grid reuses this for the same click-to-sort headers.
export function SortHeader({ label, k, sort, onSort, width, freeze }) {
  const active = sort.key === k;
  return (
    <th
      className={freeze ? `sortable ${freeze.className}` : 'sortable'}
      style={freeze ? freeze.style : { width }}
      onClick={() => onSort(k)}
    >
      {label}
      <span className={`sort-arrow${active ? '' : ' is-idle'}`}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  );
}

/**
 * "All times are Mountain Time" — said once, above the grid, instead of in every cell.
 *
 * Jeremy's ask directly: "We don't have to list the time zone in the time - just a note at the top that
 * says - all times are in Eastern, Central, Mtn, or Pacific (depending on the customer)."
 *
 * Renders the honest version when the account has no usable state, rather than defaulting to a zone.
 */
// Exported: CustomerBpa.js shows the same note (including the split-state caveat) above its grid.
export function TimeZoneNote({ note }) {
  return (
    <div className="sheet-note">
      {note ? (
        <span>
          All times below are <strong>{note.zone}</strong> — {note.stateName}, the county's own local
          time. No zone goes in the cell.
        </span>
      ) : (
        <span>
          All times below are the <strong>county's own local time</strong>. This customer's account has
          no state recorded, so the zone cannot be named here — add the state on the account to show it.
        </span>
      )}
      {note?.caveats.map((c) => (
        <span key={c} className="sheet-note-caveat">
          {c}
        </span>
      ))}
      <span style={{ color: 'var(--text-light)' }}>
        Type loosely — <code>930</code>, <code>9:30a</code> and <code>1300</code> are all tidied to a
        standard time when you save.
      </span>
    </div>
  );
}

function LinkCell({ value, placeholder, onChange }) {
  return (
    <td>
      <div className="link-cell">
        <input
          className="cell-input"
          placeholder={placeholder}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
        {/* Shown only once there is something to open. `noreferrer` because these are customer
            SharePoint and Teams URLs and there is no reason to leak this app's address to them. */}
        {value ? (
          <a className="link-out" href={value} target="_blank" rel="noreferrer" title={value}>
            ↗
          </a>
        ) : null}
      </div>
    </td>
  );
}

function DetailGrid({
  session,
  vocabulary,
  dirty,
  selected,
  onToggleSelect,
  onEdit,
  onAdd,
  onDelete,
  busy,
}) {
  const [confirmId, setConfirmId] = useState('');

  return (
    <div style={{ marginTop: 10 }}>
      {session.details.length === 0 ? (
        <div className="cell-read">
          No discussion rows. The library has no questions for this topic yet — adding them back to the
          library rather than only here is what stops the next project starting from the same blank.
        </div>
      ) : (
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th {...frozen(DETAIL_PANES, 0)} />
                <th {...frozen(DETAIL_PANES, 1)}>#</th>
                <th {...frozen(DETAIL_PANES, 2)}>Detail topic</th>
                {DETAIL_COLUMNS.map((c) => (
                  <th key={c.key} style={{ width: c.width }}>
                    {c.label}
                  </th>
                ))}
                <th style={{ width: 130 }}>Status</th>
                <th style={{ width: 62 }}>Config</th>
                {/* "Internal notes", not "Config notes" (Jeremy, 2026-07-30). The column is
                    `BpaDetail.configurationNotes` and stays that way — its schema comment already
                    describes it as internal working notes (config to-dos, ticket references,
                    data-cleanup candidates), so this is the caption catching up with what the column
                    always held. Renaming the column itself would be a migration, a Prisma regeneration
                    and an ADR-0017 allow-list edit for a word on a header. */}
                <th style={{ width: 220 }}>Internal notes</th>
                <th style={{ width: 62 }}>Follow&nbsp;up</th>
                <th style={{ width: 120 }}>Owner</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {session.details.map((d, index) => (
                <tr key={d.id} className={rowClassName(index, !!dirty[d.id])}>
                  <td {...frozen(DETAIL_PANES, 0, 'cell-check')}>
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => onToggleSelect(d.id)}
                      aria-label={`Select ${d.detailTopic}`}
                    />
                  </td>
                  <td {...frozen(DETAIL_PANES, 1, 'cell-num')}>{d.detailNumber ?? '—'}</td>
                  <td {...frozen(DETAIL_PANES, 2)}>
                    <input
                      className="cell-input"
                      value={d.detailTopic ?? ''}
                      onChange={(e) => onEdit(session.id, d.id, 'detailTopic', e.target.value)}
                    />
                  </td>
                  {DETAIL_COLUMNS.map((c) => (
                    <td key={c.key}>
                      <textarea
                        className={c.expand ? 'cell-area cell-area-expand' : 'cell-area'}
                        rows={2}
                        value={d[c.key] ?? ''}
                        onChange={(e) => onEdit(session.id, d.id, c.key, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <select
                      className="cell-select"
                      value={d.status}
                      onChange={(e) => onEdit(session.id, d.id, 'status', e.target.value)}
                    >
                      {(vocabulary?.detailStatuses ?? [d.status]).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="cell-check">
                    <input
                      type="checkbox"
                      checked={!!d.configurationNeeded}
                      onChange={(e) => onEdit(session.id, d.id, 'configurationNeeded', e.target.checked)}
                      aria-label="Configuration needed"
                    />
                  </td>
                  <td>
                    <textarea
                      className="cell-area"
                      rows={2}
                      placeholder="Not shown to the customer"
                      value={d.configurationNotes ?? ''}
                      onChange={(e) => onEdit(session.id, d.id, 'configurationNotes', e.target.value)}
                    />
                  </td>
                  <td className="cell-check">
                    <input
                      type="checkbox"
                      checked={!!d.followUpNeeded}
                      onChange={(e) => onEdit(session.id, d.id, 'followUpNeeded', e.target.checked)}
                      aria-label="Follow up needed"
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      list="bpa-user-list"
                      placeholder="Type to search…"
                      value={d.followUpOwner ?? ''}
                      onChange={(e) => onEdit(session.id, d.id, 'followUpOwner', e.target.value)}
                    />
                  </td>
                  <td>
                    {confirmId === d.id ? (
                      <button
                        className="icon-btn"
                        onClick={() => onDelete(d.id)}
                        disabled={!!busy}
                        title="Confirm delete"
                      >
                        ✓?
                      </button>
                    ) : (
                      <button
                        className="icon-btn"
                        onClick={() => setConfirmId(d.id)}
                        title="Delete this row"
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="sheet-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
        <button className="btn btn-outline" onClick={onAdd} disabled={!!busy}>
          + Row
        </button>
      </div>
    </div>
  );
}

/**
 * Scopes the exported report to one or more modules — a DAS milestone sign-off usually covers a few
 * modules from one BPA iteration, not the whole project.
 *
 * Reuses `.modal-*` rather than adding new CSS: this and `SetupModal` are the only two dialogs on the
 * page and there was nothing about this one that needed a different look.
 */
function ExportModal({ sessionModules, selectedIds, onToggle, onSelectAll, onSelectNone, onClose, onExport }) {
  const allSelected = selectedIds.size === 0 || selectedIds.size === sessionModules.length;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Export report</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-section">
            <div className="modal-section-title">Modules to include</div>
            {sessionModules.length === 0 ? (
              <div className="cell-read" style={{ padding: 0 }}>
                No sessions have a module yet — the report will include everything.
              </div>
            ) : (
              <>
                <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
                  <span className="cell-read" style={{ padding: 0, fontSize: 12 }}>
                    {allSelected ? 'All modules' : `${selectedIds.size} of ${sessionModules.length}`}
                  </span>
                  <div className="spacer" />
                  <button className="btn btn-outline" onClick={onSelectAll}>
                    All
                  </button>
                  <button className="btn btn-outline" onClick={onSelectNone}>
                    None
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
                  {sessionModules.map((m) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={allSelected || selectedIds.has(m.id)}
                        onChange={() => onToggle(m.id)}
                      />
                      {m.code} — {m.name}
                    </label>
                  ))}
                </div>
              </>
            )}
            <p className="cell-read" style={{ padding: '10px 0 0', fontSize: 12 }}>
              Leave everything checked to include all modules. Sessions with no module (added by hand)
              are always included. Internal-only notes and follow-ups are never exported — this is a
              customer-facing document.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onExport}>
            Export .xlsx
          </button>
        </div>
      </div>
    </div>
  );
}

function SetupModal({
  workbook,
  flatModules,
  inScopeIds,
  iterations,
  seedPreview,
  seedSelectedIds,
  sowResult,
  busy,
  onClose,
  onAddFromSow,
  onAddModule,
  onSetIteration,
  onRemoveModule,
  onPreviewSeed,
  onCommitSeed,
  onCancelPreview,
  onToggleSeedRow,
  onSelectAllSeedRows,
  onSelectNoneSeedRows,
  onRemoveUnstarted,
  onMaintainLibrary,
}) {
  // Rows count only what's actually selected — the commit button's own preview of what it's about
  // to create, not the whole candidate set once some rows have been unchecked.
  const plannedRows = (seedPreview?.planned ?? [])
    .filter((p) => seedSelectedIds.has(p.topicTemplateId))
    .reduce((n, p) => n + p.detailTopics.length, 0);
  // With exactly one iteration in the schedule, every module belongs to it and asking would be busy
  // work — so the picker only appears when there is a real choice to make.
  const oneIteration = iterations.length === 1;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Modules &amp; seeding</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-section">
            <div className="modal-section-title">Modules in scope</div>
            <div className="sheet-toolbar">
              <button className="btn btn-outline" onClick={onAddFromSow} disabled={!!busy}>
                {busy === 'sow' ? 'Reading SOW…' : 'Add from SOW'}
              </button>
              <div className="spacer" />
              <select
                className="form-select"
                style={{ maxWidth: 280 }}
                value=""
                onChange={(e) => onAddModule(e.target.value)}
                aria-label="Add a module"
              >
                <option value="">Add a module by hand…</option>
                {flatModules
                  .filter((m) => !inScopeIds.has(m.id))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {' '.repeat(m.depth * 3)}
                      {m.name}
                    </option>
                  ))}
              </select>
            </div>

            {sowResult && (
              <div className={`notice ${sowResult.unmatched.length ? 'notice-warn' : 'notice-info'}`}>
                Matched {sowResult.matched.length} of{' '}
                {sowResult.matched.length + sowResult.unmatched.length} module(s) named in the SOW
                {sowResult.alreadyInScope.length
                  ? `; ${sowResult.alreadyInScope.length} already in scope`
                  : ''}
                .
                {sowResult.unmatched.length ? (
                  <>
                    {' '}
                    <strong>Not recognized:</strong> {sowResult.unmatched.join(', ')} — add these by
                    hand, or add them to the taxonomy so the next project matches them.
                  </>
                ) : null}
              </div>
            )}

            {workbook.modulesInScope.length === 0 ? (
              <div className="cell-read" style={{ padding: 0 }}>
                None recorded yet.
              </div>
            ) : (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th style={{ width: 90 }}>Code</th>
                      {!oneIteration && <th style={{ width: 250 }}>BPA iteration</th>}
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {workbook.modulesInScope.map((pm) => (
                      <tr key={pm.id}>
                        <td className="cell-read">{pm.module.name}</td>
                        <td className="cell-read">{pm.module.code}</td>
                        {!oneIteration && (
                          <td>
                            {iterations.length === 0 ? (
                              <span className="cell-read" style={{ fontSize: 12 }}>
                                none in schedule
                              </span>
                            ) : (
                              <select
                                className="cell-select"
                                value={pm.bpaIterationTaskId ?? ''}
                                onChange={(e) => onSetIteration(pm.id, e.target.value)}
                              >
                                <option value="">— unassigned —</option>
                                {iterations.map((it) => (
                                  <option key={it.id} value={it.id}>
                                    {it.title}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        )}
                        <td>
                          <button
                            className="icon-btn"
                            onClick={() => onRemoveModule(pm.id)}
                            disabled={!!busy}
                            title="Remove from scope"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {oneIteration && (
              <div className="cell-read" style={{ padding: '10px 0 0', fontSize: 12 }}>
                The schedule has one BPA iteration (<strong>{iterations[0].title}</strong>), so every
                module belongs to it. The per-module picker appears if more iterations are added.
              </div>
            )}
          </div>

          <div className="modal-section">
            <div className="modal-section-title">Seed sessions from the library</div>
            {seedPreview ? (
              <>
                {seedPreview.warnings.map((w) => (
                  <div key={w} className="notice notice-warn">
                    {w}
                  </div>
                ))}
                <p className="cell-read" style={{ padding: '0 0 10px' }}>
                  <strong>{seedPreview.planned.length}</strong> candidate session(s) and{' '}
                  <strong>{plannedRows}</strong> discussion row(s) selected, from{' '}
                  {seedPreview.modulesConsidered
                    .map((m) => (m.viaParent ? `${m.code} (via ${m.viaParent})` : m.code))
                    .join(', ')}
                  {seedPreview.skipped.length
                    ? `. ${seedPreview.skipped.length} already seeded and will be left alone.`
                    : '.'}{' '}
                  {/* Which market's topics were included, alongside every National one. Stated because
                      a plan that silently omitted another market's session would otherwise look like a
                      library gap rather than a scoping decision (ADR 0018). */}
                  Includes all National topics
                  {seedPreview.market
                    ? ` plus any for the ${seedPreview.market} market.`
                    : ' only — this account has no state, so it has no market.'}
                </p>
                <div className="sheet-toolbar" style={{ marginBottom: 8 }}>
                  <span className="cell-read" style={{ padding: 0, fontSize: 12 }}>
                    {seedSelectedIds.size} of {seedPreview.planned.length} selected
                  </span>
                  <div className="spacer" />
                  <button className="btn btn-outline" onClick={onSelectAllSeedRows}>
                    All
                  </button>
                  <button className="btn btn-outline" onClick={onSelectNoneSeedRows}>
                    None
                  </button>
                </div>
                <div className="sheet-wrap" style={{ maxHeight: 220, overflowY: 'auto' }}>
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }} />
                        <th style={{ width: 150 }}>Module</th>
                        <th>Session</th>
                        <th style={{ width: 80 }}>Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seedPreview.planned.map((p) => (
                        <tr key={p.topicTemplateId}>
                          <td className="cell-read">
                            <input
                              type="checkbox"
                              checked={seedSelectedIds.has(p.topicTemplateId)}
                              onChange={() => onToggleSeedRow(p.topicTemplateId)}
                            />
                          </td>
                          <td className="cell-read">{p.moduleName}</td>
                          <td className="cell-read">{p.topic}</td>
                          <td className="cell-num">{p.detailTopics.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="sheet-toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
                  <button
                    className="btn btn-success"
                    onClick={onCommitSeed}
                    disabled={!!busy || seedSelectedIds.size === 0}
                  >
                    {busy === 'seeding'
                      ? 'Creating…'
                      : `Create ${seedSelectedIds.size} session(s)`}
                  </button>
                  <button className="btn btn-outline" onClick={onCancelPreview}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div className="sheet-toolbar" style={{ marginBottom: 0 }}>
                <button
                  className="btn btn-outline"
                  onClick={onPreviewSeed}
                  disabled={!!busy || workbook.modulesInScope.length === 0}
                >
                  {busy === 'previewing' ? 'Checking…' : 'Preview seed from library'}
                </button>
                <button className="btn btn-outline" onClick={onRemoveUnstarted} disabled={!!busy}>
                  {busy === 'clearing' ? 'Removing…' : 'Remove unstarted sessions'}
                </button>
                <button
                  className="btn btn-outline"
                  onClick={onMaintainLibrary}
                  disabled={!!busy}
                  title="Add, edit or retire the standard BPA topics and discussion rows"
                >
                  Maintain BPA Library
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The standard library, editable — the screen that stops "add this question to the library" meaning a
 * pull request.
 *
 * **Why it lives inside the tracker rather than being its own page.** Jeremy's framing: pick `BPA
 * Library` in the project dropdown "to keep the UI the same". A library topic *is* a session and a
 * library detail row *is* a discussion row, minus the columns that record what happened, so the same
 * two-level expand-and-edit grid is the right shape. A separate page would have been a second thing to
 * learn for the same job.
 *
 * **Self-contained state, deliberately.** Nothing here shares state with the workbook — the two modes
 * are never on screen together, and folding another six `useState` calls into a component that already
 * has twenty would have made every one of them harder to follow.
 *
 * Only ever rendered for an admin with maintenance mode on (ADR 0020). It does not re-check that: the
 * caller owns the gate, and the API owns the enforcement.
 */
function LibraryEditor({ flatModules, vocabulary }) {
  const [moduleId, setModuleId] = useState('');
  const [topics, setTopics] = useState([]);
  // topicTemplateId -> its detail-template rows. Fetched when a topic is first expanded rather than
  // eagerly for the whole module: a module can carry a dozen topics and nobody edits all of them at
  // once. Kept after collapsing so re-expanding is instant.
  const [detailsByTopic, setDetailsByTopic] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());

  const [dirtyTopics, setDirtyTopics] = useState({});
  const [dirtyDetails, setDirtyDetails] = useState({});

  const [confirming, setConfirming] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [syncResult, setSyncResult] = useState(null);

  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const dirtyCount = Object.keys(dirtyTopics).length + Object.keys(dirtyDetails).length;
  const states = vocabulary?.states ?? [];

  const loadTopics = useCallback(async (id) => {
    if (!id) {
      setTopics([]);
      return;
    }
    setBusy('loading');
    setError('');
    try {
      // includeInactive: this is the library-admin screen, and a retired topic is exactly what someone
      // comes here to un-retire. The normal read hides them (see TopicTemplatesService.findAll).
      const rows = await apiFetch(`/bpa-topic-templates?moduleId=${id}&includeInactive=true`);
      setTopics(rows);
      setDetailsByTopic({});
      setExpanded(new Set());
      setDirtyTopics({});
      setDirtyDetails({});
    } catch (e) {
      setError(e.message);
      setTopics([]);
    } finally {
      setBusy('');
    }
  }, []);

  useEffect(() => {
    loadTopics(moduleId);
  }, [moduleId, loadTopics]);

  const run = async (label, fn, successMessage) => {
    setBusy(label);
    setError('');
    setNotice('');
    try {
      const message = await fn();
      if (message ?? successMessage) setNotice(message ?? successMessage);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
      setConfirming('');
    }
  };

  const toggleTopic = async (id) => {
    const next = new Set(expanded);
    if (next.has(id)) {
      next.delete(id);
      setExpanded(next);
      return;
    }
    next.add(id);
    setExpanded(next);
    if (detailsByTopic[id]) return;
    try {
      const full = await apiFetch(`/bpa-topic-templates/${id}`);
      setDetailsByTopic((prev) => ({ ...prev, [id]: full.detailTemplates }));
    } catch (e) {
      setError(e.message);
    }
  };

  // Same immediate-echo-plus-remember pattern as the workbook grid: the typed character shows at once
  // and Save flushes what changed.
  const editTopic = (id, key, value) => {
    setTopics((rows) => rows.map((t) => (t.id === id ? { ...t, [key]: value } : t)));
    setDirtyTopics((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  };

  const editDetail = (topicId, id, key, value) => {
    setDetailsByTopic((prev) => ({
      ...prev,
      [topicId]: prev[topicId].map((d) => (d.id === id ? { ...d, [key]: value } : d)),
    }));
    setDirtyDetails((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  };

  /**
   * Switching a topic to National clears its markets in the same edit.
   *
   * Both halves are sent because the API applies the same rule (`resolveMarkets`) — doing it here too
   * keeps the grid from showing state chips against a National topic between the change and the save,
   * which would read as "these still apply".
   */
  const setScope = (id, scope) => {
    setTopics((rows) =>
      rows.map((t) => (t.id === id ? { ...t, scope, markets: scope === 'Market' ? t.markets : [] } : t)),
    );
    setDirtyTopics((prev) => ({
      ...prev,
      [id]: { ...prev[id], scope, marketStates: scope === 'Market' ? undefined : [] },
    }));
  };

  const setMarkets = (id, stateCodes) => {
    setTopics((rows) =>
      rows.map((t) => (t.id === id ? { ...t, markets: stateCodes.map((c) => ({ stateCode: c })) } : t)),
    );
    setDirtyTopics((prev) => ({ ...prev, [id]: { ...prev[id], marketStates: stateCodes } }));
  };

  const saveAll = () =>
    run('saving', async () => {
      const topicPatches = Object.entries(dirtyTopics);
      const detailPatches = Object.entries(dirtyDetails);
      // Individual PATCHes rather than a bulk route, for the same reason the workbook's session edits
      // are individual: a module's library is a dozen topics and a few dozen rows, and adding two bulk
      // endpoints to save a handful of round trips would be surface for its own sake.
      await Promise.all([
        ...topicPatches.map(([id, patch]) =>
          apiFetch(`/bpa-topic-templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
        ),
        ...detailPatches.map(([id, patch]) =>
          apiFetch(`/bpa-detail-templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
        ),
      ]);
      // Cleared only after every write lands. A market topic saved with no states is rejected by the
      // API with a message that says so, and keeping the pending edits means the fix is one field away
      // rather than a retype.
      setDirtyTopics({});
      setDirtyDetails({});
      await loadTopics(moduleId);
      return `Saved ${topicPatches.length} topic(s) and ${detailPatches.length} row(s).`;
    });

  const addTopic = () =>
    run('adding', async () => {
      await apiFetch('/bpa-topic-templates', {
        method: 'POST',
        // sequence 1 so a new topic lands at the top of the module's list where it can be filled in,
        // rather than after everything at the default 99.
        body: JSON.stringify({ moduleId, title: 'New topic', sequence: 1 }),
      });
      await loadTopics(moduleId);
      return '';
    });

  const addDetail = (topicId) =>
    run('adding', async () => {
      const rows = detailsByTopic[topicId] ?? [];
      const next = Math.max(0, ...rows.map((d) => d.sequence ?? 0)) + 1;
      const created = await apiFetch('/bpa-detail-templates', {
        method: 'POST',
        body: JSON.stringify({
          topicTemplateId: topicId,
          detailTopic: 'New discussion row',
          sequence: next,
        }),
      });
      // Appended locally rather than refetching the module: the topic list is unaffected, and a full
      // reload would drop every other topic's pending edits and collapse the tree.
      setDetailsByTopic((prev) => ({ ...prev, [topicId]: [...(prev[topicId] ?? []), created] }));
      return '';
    });

  const removeTopic = (id) =>
    run('deleting', async () => {
      await apiFetch(`/bpa-topic-templates/${id}`, { method: 'DELETE' });
      await loadTopics(moduleId);
      return 'Topic removed from the library.';
    });

  const removeDetail = (topicId, id) =>
    run('deleting', async () => {
      await apiFetch(`/bpa-detail-templates/${id}`, { method: 'DELETE' });
      setDetailsByTopic((prev) => ({
        ...prev,
        [topicId]: prev[topicId].filter((d) => d.id !== id),
      }));
      return 'Library row removed.';
    });

  const syncFromCode = () =>
    run('syncing', async () => {
      const result = await apiFetch('/bpa/library/sync', { method: 'POST' });
      setSyncResult(result);
      if (moduleId) await loadTopics(moduleId);
      return '';
    });

  return (
    <>
      {error && <div className="notice notice-error">{error}</div>}
      {notice && <div className="notice notice-info">{notice}</div>}

      <div className="card">
        <div className="sheet-toolbar" style={{ marginBottom: 0 }}>
          <select
            className="form-select"
            style={{ maxWidth: 320 }}
            value={moduleId}
            onChange={(e) => setModuleId(e.target.value)}
            aria-label="Module"
          >
            <option value="">Select a module…</option>
            {flatModules.map((m) => (
              <option key={m.id} value={m.id}>
                {' '.repeat(m.depth * 3)}
                {m.name} ({m.code})
              </option>
            ))}
          </select>
          <div className="spacer" />
          {isAdmin && (
            <button
              className="btn btn-outline"
              onClick={syncFromCode}
              disabled={!!busy}
              title="Load the code-defined library (packages/shared) into the database. Additive — never overwrites captured answers."
            >
              {busy === 'syncing' ? 'Syncing…' : 'Sync from code'}
            </button>
          )}
          <button className="btn btn-outline" onClick={addTopic} disabled={!!busy || !moduleId}>
            + Topic
          </button>
          <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyCount}>
            {busy === 'saving' ? 'Saving…' : dirtyCount ? `Save ${dirtyCount} change(s)` : 'Saved'}
          </button>
        </div>
      </div>

      {syncResult && (
        <div className={`notice ${syncResult.orphans.topics.length ? 'notice-warn' : 'notice-info'}`}>
          Synced from code: {syncResult.modulesCreated} module(s) and {syncResult.topicsCreated} topic(s)
          added, {syncResult.topicsUpdated} topic(s) and {syncResult.detailsUpdated} row(s) refreshed.
          {syncResult.orphans.topics.length ? (
            <>
              {' '}
              <strong>Only in the database:</strong> {syncResult.orphans.topics.join('; ')} — kept, not
              deleted. Anything added here that should survive a fresh environment belongs in
              <code> BPA_TOPIC_LIBRARY</code> too.
            </>
          ) : null}
        </div>
      )}

      {!moduleId ? (
        <div className="card">
          <div className="cell-read" style={{ padding: 0 }}>
            Pick a module to edit its standard BPA topics and the discussion rows each one seeds. A topic
            marked <strong>National</strong> seeds for every project; <strong>Market</strong> seeds only
            for projects whose customer is in one of the states you list against it.
          </div>
        </div>
      ) : (
        <div className="card">
          {busy === 'loading' ? (
            <div className="cell-read" style={{ padding: 0 }}>
              Loading…
            </div>
          ) : topics.length === 0 ? (
            <div className="cell-read" style={{ padding: 0 }}>
              This module has no library topics yet. Add one, or run <strong>Sync from code</strong> to
              load the standard set.
            </div>
          ) : (
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th {...frozen(LIBRARY_TOPIC_PANES, 0)} />
                    <th {...frozen(LIBRARY_TOPIC_PANES, 1)}>Seq</th>
                    <th {...frozen(LIBRARY_TOPIC_PANES, 2)}>Topic</th>
                    <th style={{ width: 120 }}>Scope</th>
                    <th style={{ width: 240 }}>Markets</th>
                    <th style={{ width: 380 }}>Purpose</th>
                    <th style={{ width: 62 }}>Active</th>
                    <th style={{ width: 60 }}>Rows</th>
                    <th style={{ width: 40 }} />
                  </tr>
                </thead>
                <tbody>
                  {topics.map((t, index) => (
                    <React.Fragment key={t.id}>
                      <tr className={rowClassName(index, !!dirtyTopics[t.id])}>
                        <td {...frozen(LIBRARY_TOPIC_PANES, 0)}>
                          <button
                            className="expand-btn"
                            onClick={() => toggleTopic(t.id)}
                            aria-label={expanded.has(t.id) ? 'Collapse' : 'Expand'}
                            style={{ padding: '7px 10px' }}
                          >
                            {expanded.has(t.id) ? '▾' : '▸'}
                          </button>
                        </td>
                        <SequenceCell
                          {...frozen(LIBRARY_TOPIC_PANES, 1)}
                          value={t.sequence}
                          onCommit={(n) => editTopic(t.id, 'sequence', n)}
                        />
                        <td {...frozen(LIBRARY_TOPIC_PANES, 2)}>
                          <input
                            className="cell-input"
                            value={t.title ?? ''}
                            onChange={(e) => editTopic(t.id, 'title', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="cell-select"
                            value={t.scope}
                            onChange={(e) => setScope(t.id, e.target.value)}
                            aria-label="Scope"
                          >
                            {(vocabulary?.topicScopes ?? [t.scope]).map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        </td>
                        <MarketCell
                          disabled={t.scope !== 'Market'}
                          selected={(t.markets ?? []).map((m) => m.stateCode)}
                          states={states}
                          onChange={(codes) => setMarkets(t.id, codes)}
                        />
                        <td>
                          <textarea
                            className="cell-area"
                            rows={2}
                            placeholder="What this session has to land, for the BA reading it beforehand"
                            value={t.purpose ?? ''}
                            onChange={(e) => editTopic(t.id, 'purpose', e.target.value)}
                          />
                        </td>
                        <td className="cell-check">
                          <input
                            type="checkbox"
                            checked={!!t.isActive}
                            onChange={(e) => editTopic(t.id, 'isActive', e.target.checked)}
                            aria-label="Active"
                          />
                        </td>
                        <td className="cell-num">{t._count?.detailTemplates ?? 0}</td>
                        <td>
                          {confirming === t.id ? (
                            <button
                              className="icon-btn"
                              onClick={() => removeTopic(t.id)}
                              disabled={!!busy}
                              title="Confirm — refused if any project has already seeded from this topic"
                            >
                              ✓?
                            </button>
                          ) : (
                            <button
                              className="icon-btn"
                              onClick={() => setConfirming(t.id)}
                              title="Delete this library topic. Untick Active instead to retire one that has been used."
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>

                      {expanded.has(t.id) && (
                        <tr>
                          <td colSpan={9} style={{ padding: '0 0 10px 34px', background: 'var(--light-bg)' }}>
                            <LibraryDetailGrid
                              rows={detailsByTopic[t.id]}
                              dirty={dirtyDetails}
                              busy={busy}
                              onEdit={(id, key, value) => editDetail(t.id, id, key, value)}
                              onAdd={() => addDetail(t.id)}
                              onDelete={(id) => removeDetail(t.id, id)}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * A sort-order cell that keeps its own draft text.
 *
 * The reason it is not a plain bound `<input type="number">`: clearing the box to retype gives
 * `Number('') === 0`, and both template DTOs validate `sequence` with `@Min(1)`. Queuing that would make
 * the next Save 400 on a number the user was halfway through typing, taking every other pending edit in
 * the grid down with it. So the draft is local and only a valid 1–999 integer is ever handed upwards.
 *
 * Reverts to the stored value on blur if what is in the box isn't usable, rather than leaving a cell
 * showing something that was never saved.
 */
function SequenceCell({ value, onCommit, ...cellProps }) {
  const [draft, setDraft] = useState(String(value ?? ''));

  // Re-sync when the row is replaced by a refetch after Save. Comparing against the draft rather than
  // tracking the previous prop keeps this from fighting the user mid-edit.
  useEffect(() => {
    setDraft(String(value ?? ''));
  }, [value]);

  const commit = (raw) => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isInteger(parsed) && parsed >= 1 && parsed <= 999) onCommit(parsed);
  };

  return (
    <td {...cellProps}>
      <input
        className="cell-input"
        type="number"
        min={1}
        max={999}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          commit(e.target.value);
        }}
        onBlur={() => {
          const parsed = Number.parseInt(draft, 10);
          if (!Number.isInteger(parsed) || parsed < 1 || parsed > 999) setDraft(String(value ?? ''));
        }}
        aria-label="Sort order"
      />
    </td>
  );
}

/**
 * The states a market-scoped topic covers, as removable chips plus an add-picker.
 *
 * Not a `<select multiple>`: that needs ctrl-click to deselect, shows four rows of a fifty-item list,
 * and gives no compact read of what is currently selected — which is the thing you are scanning a
 * library grid for.
 */
function MarketCell({ disabled, selected, states, onChange }) {
  if (disabled) {
    return (
      <td className="cell-read" style={{ fontSize: 12, color: 'var(--text-light)' }}>
        every market
      </td>
    );
  }

  const available = states.filter((s) => !selected.includes(s.code));

  return (
    <td>
      <div className="chip-cell">
        {selected.map((code) => (
          <span key={code} className="chip">
            {code}
            <button
              onClick={() => onChange(selected.filter((c) => c !== code))}
              aria-label={`Remove ${code}`}
              title={`Remove ${code}`}
            >
              ×
            </button>
          </span>
        ))}
        {/* Value pinned to '' so the picker always reads "add" rather than showing the last state
            chosen, which would look like a single-select that had been set. */}
        <select
          value=""
          onChange={(e) => e.target.value && onChange([...selected, e.target.value])}
          aria-label="Add a state to this market"
        >
          <option value="">+ state</option>
          {available.map((s) => (
            <option key={s.code} value={s.code}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
        {selected.length === 0 && (
          <span style={{ fontSize: 11, color: 'var(--warn-text)' }}>needs at least one</span>
        )}
      </div>
    </td>
  );
}

/** A library topic's planned discussion rows — the seed of what a session's grid starts life as. */
function LibraryDetailGrid({ rows, dirty, busy, onEdit, onAdd, onDelete }) {
  const [confirmId, setConfirmId] = useState('');

  if (!rows) {
    return (
      <div className="cell-read" style={{ marginTop: 10 }}>
        Loading…
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {rows.length === 0 ? (
        <div className="cell-read">
          No planned rows yet. A topic with none seeds as an agenda item over an empty grid — writing the
          questions here is what stops the next project starting from the same blank.
        </div>
      ) : (
        <div className="sheet-wrap">
          <table className="sheet">
            <thead>
              <tr>
                <th {...frozen(LIBRARY_DETAIL_PANES, 0)}>Seq</th>
                <th {...frozen(LIBRARY_DETAIL_PANES, 1)}>Detail topic</th>
                <th style={{ width: 420 }}>Discussion points</th>
                <th style={{ width: 420 }}>Customer questions</th>
                <th style={{ width: 62 }}>Active</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((d, index) => (
                <tr key={d.id} className={rowClassName(index, !!dirty[d.id])}>
                  <SequenceCell
                    {...frozen(LIBRARY_DETAIL_PANES, 0)}
                    value={d.sequence}
                    onCommit={(n) => onEdit(d.id, 'sequence', n)}
                  />
                  <td {...frozen(LIBRARY_DETAIL_PANES, 1)}>
                    <input
                      className="cell-input"
                      value={d.detailTopic ?? ''}
                      onChange={(e) => onEdit(d.id, 'detailTopic', e.target.value)}
                    />
                  </td>
                  <td>
                    <textarea
                      className="cell-area cell-area-expand"
                      rows={2}
                      value={d.discussionPoints ?? ''}
                      onChange={(e) => onEdit(d.id, 'discussionPoints', e.target.value)}
                    />
                  </td>
                  <td>
                    <textarea
                      className="cell-area cell-area-expand"
                      rows={2}
                      value={d.customerQuestions ?? ''}
                      onChange={(e) => onEdit(d.id, 'customerQuestions', e.target.value)}
                    />
                  </td>
                  <td className="cell-check">
                    <input
                      type="checkbox"
                      checked={!!d.isActive}
                      onChange={(e) => onEdit(d.id, 'isActive', e.target.checked)}
                      aria-label="Active"
                    />
                  </td>
                  <td>
                    {confirmId === d.id ? (
                      <button
                        className="icon-btn"
                        onClick={() => onDelete(d.id)}
                        disabled={!!busy}
                        title="Confirm — refused if any project has already seeded from this row"
                      >
                        ✓?
                      </button>
                    ) : (
                      <button
                        className="icon-btn"
                        onClick={() => setConfirmId(d.id)}
                        title="Delete this library row. Untick Active instead to retire one that has been used."
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="sheet-toolbar" style={{ marginTop: 10, marginBottom: 0 }}>
        <button className="btn btn-outline" onClick={onAdd} disabled={!!busy}>
          + Row
        </button>
      </div>
    </div>
  );
}

// Exported: CustomerBpa.js's stat row reuses this (minus the two internal-only stats -- config
// needed/open follow-ups key off fields ADR 0017 excludes from the customer response entirely).
export function Stat({ value, label, tone }) {
  return (
    <div className={`stat${tone ? ` is-${tone}` : ''}`}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function toggle(setter, id) {
  setter((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
}

export default BPATracker;
